import { ActionIcon, Badge, Card, Code, Collapse, Group, Pagination, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { IconChevronDown, IconChevronRight, IconHistory, IconSearch } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Fragment, useState } from 'react';
import type { ActivityLogDto, Paginated } from '@mexp/shared';
import { http } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { formatDateTime, fromNow } from '@/lib/format';

const PAGE_SIZE = 50;

function actionColor(action: string): string {
  if (/failed|locked|returned|cancel/i.test(action)) return 'red';
  if (/login|logout/i.test(action)) return 'gray';
  if (/created|added|submitted|sent/i.test(action)) return 'blue';
  if (/verified|approved|completed/i.test(action)) return 'teal';
  return 'brand';
}

export function ActivityLogPage() {
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [action, setAction] = useState<string | null>(null);
  const [range, setRange] = useState<[string | null, string | null]>([null, null]);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);

  const actions = useQuery({ queryKey: ['activity', 'actions'], queryFn: () => http.get<{ items: string[] }>('/activity-log/actions') });
  const list = useQuery({
    queryKey: ['activity', { q, action, range, page }],
    queryFn: () =>
      http.get<Paginated<ActivityLogDto>>('/activity-log', {
        q,
        action,
        from: range[0] ? dayjs(range[0]).startOf('day').toISOString() : undefined,
        to: range[1] ? dayjs(range[1]).endOf('day').toISOString() : undefined,
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;

  return (
    <>
      <PageHeader title="Activity log" description="An audit trail of sign-ins and every change made in MExP, including entries imported from the old system." />
      <Card p={0}>
        <Group p="md" gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search person, action or record"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
            w={{ base: '100%', sm: 280 }}
          />
          <Select
            placeholder="Any action"
            clearable
            searchable
            data={actions.data?.items ?? []}
            value={action}
            onChange={(v) => {
              setAction(v);
              setPage(1);
            }}
            w={220}
          />
          <DatePickerInput
            type="range"
            placeholder="Any date"
            clearable
            value={range}
            onChange={(v) => {
              setRange(v as [string | null, string | null]);
              setPage(1);
            }}
            w={260}
            maxDate={new Date()}
          />
        </Group>
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState icon={IconHistory} title="No activity matches" />
        ) : (
          <div className="table-scroll">
            <Table miw={860}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <Table.Th>When</Table.Th>
                  <Table.Th>Who</Table.Th>
                  <Table.Th>Action</Table.Th>
                  <Table.Th>Record</Table.Th>
                  <Table.Th>Details</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.items.map((e) => {
                  const hasDetail = e.before !== null || e.after !== null;
                  const expanded = open === e.id;
                  return (
                    <Fragment key={e.id}>
                      <Table.Tr>
                        <Table.Td>
                          {hasDetail && (
                            <ActionIcon size="sm" onClick={() => setOpen(expanded ? null : e.id)} aria-label="Show details">
                              {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                            </ActionIcon>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={formatDateTime(e.createdAt)}>
                            <Text fz="sm" className="nowrap">
                              {fromNow(e.createdAt)}
                            </Text>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Text fz="sm">{e.actorEmail}</Text>
                          {e.actorRole && (
                            <Text fz="xs" c="dimmed">
                              {e.actorRole}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Badge color={actionColor(e.action)} variant="light" tt="none">
                              {e.action}
                            </Badge>
                            {e.legacy && (
                              <Badge size="xs" variant="outline" color="gray">
                                Old system
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text fz="xs" c="dimmed" ff="monospace">
                            {[e.targetType, e.targetId].filter(Boolean).join(' · ') || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fz="sm" lineClamp={2}>
                            {e.summary ?? '—'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                      {hasDetail && (
                        <Table.Tr style={{ display: expanded ? undefined : 'none' }}>
                          <Table.Td colSpan={6} p={0}>
                            <Collapse in={expanded}>
                              <Group grow align="flex-start" p="md" gap="md">
                                <Stack gap={4}>
                                  <Text fz="xs" fw={600} c="dimmed">
                                    BEFORE
                                  </Text>
                                  <Code block>{JSON.stringify(e.before, null, 2)}</Code>
                                </Stack>
                                <Stack gap={4}>
                                  <Text fz="xs" fw={600} c="dimmed">
                                    AFTER
                                  </Text>
                                  <Code block>{JSON.stringify(e.after, null, 2)}</Code>
                                </Stack>
                              </Group>
                            </Collapse>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        )}
        {list.data && (
          <Group justify="space-between" p="md">
            <Text fz="sm" c="dimmed">
              {list.data.total} entr{list.data.total === 1 ? 'y' : 'ies'}
            </Text>
            {pages > 1 && <Pagination total={pages} value={page} onChange={setPage} />}
          </Group>
        )}
      </Card>
    </>
  );
}
